import { captureLogger as logger } from '../../configure/logger-facade';
import { isDeviceInUseDOMException } from '../../errors';
import { DeviceInUseError, MicPermissionError } from '../../types';

/**
 * Resolves when microphone access is permitted; the value says how.
 * 'query-granted' answered from the Permissions API without touching the device;
 * 'stream-verified' acquired and stopped a stream, so the device is free too.
 */
export const getMicPermission = async (): Promise<
  'query-granted' | 'stream-verified'
> => {
  // Try Permissions API first if available.
  if (navigator.permissions && navigator.permissions.query) {
    try {
      // Some browsers support query but don't have microphone as a value
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone',
      });
      if (permissionStatus.state === 'granted') return 'query-granted';
    } catch {
      logger.debug({ event_type: 'MIC_PERMISSION_QUERY_FAILED' });
    }
  }

  // Fallback to stream method.
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop()); // Immediately stop the stream
  } catch (err) {
    // An acquisition failure is not necessarily a permission problem —
    // mislabelling it would show "allow microphone access" copy even though
    // the browser may have failed because of contention or a device issue.
    if (isDeviceInUseDOMException(err)) {
      // The typed error replaces the DOMException, so log here — the only
      // point on this path where NotReadableError (Chrome/Edge) vs
      // AbortError (Firefox) stays distinguishable.
      logger.warn({
        event_type: 'MIC_DEVICE_IN_USE',
        details: { name: err.name, message: err.message },
      });
      throw new DeviceInUseError('microphone device in use', { cause: err });
    }
    // The typed error replaces the rejection here too, so this is the only
    // point where NotAllowedError vs NotFoundError vs SecurityError (or a
    // non-Error value from older WebKit) stays distinguishable in telemetry.
    logger.warn({
      event_type: 'MIC_PERMISSION_STREAM_FAILED',
      details:
        err instanceof Error || err instanceof DOMException
          ? { name: err.name, message: err.message }
          : { name: 'UnknownError', message: String(err) },
    });
    throw new MicPermissionError('mic disallowed', { cause: err });
  }
  return 'stream-verified';
};
